import { type PaletteKey, PixelCanvas } from '../../lib';
import type { Part, RibbonKey } from '../body';
import type { Worn } from '../headwear';
import { FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

/**
 * The Creator set (ADR-0138): the Creator Program's rewards, never sold (content `PROGRAM_ITEMS`: the cap at impact 250,
 * the jacket at 500), with the creators' gun skin (ADR-0128) its third piece. A streamer's kit in the Creator violet and
 * white: a satin cap worn backwards under a white gaming headset with a mic, and a varsity jacket.
 *
 * The cap is headwear tied on (`Worn`, ADR-0089): the headset must move with the head (a hat lags it a frame and would
 * slide the cups off the ears), and stays on knocked out. Its silhouette is its own at game zoom: the white band's arc
 * over a violet dome, the bill out over the back of the head, a cup either side. The team reads on the cap's trim (a band
 * over the brow, as on the sun hat), the bill, the near cup's plate (a white ▶ on it, the set's emblem) and on the jacket
 * the ▶ badge, the collar and the cuffs. Procedural over a few shapes (the dome, the band's and the boom's curves, the
 * cups' ellipses), lit from the top-left, so standing and lying agree. The jacket: violet satin with white leather
 * sleeves, the team's ribbed cuffs and collar, white snaps, a white stripe in its dark ribbed hem.
 *
 * Rubric (review sheets at 4×-9× and 1:1, 0.67 and 0.5 on grass, lane and slate, every team, idle, run and dead, six
 * skins, beside the gnome, reaper and oni; the wardrobe's tiles): cap S5 R5 P4 L4 C4 A4 F4, lying S4 R4 P4 L4 C4 F4,
 * jacket S4 R3 P4 L4 C4 F4. Still weak: the gun hides most of the jacket in a match (its white sleeve, cuff and badge
 * carry it), and with the Creator gun its violet runs into the gun's; the cap's sheen is a hard band at 8×; the mic's foam
 * reads as a dot by the mouth at 1:1; alone (the wardrobe's hat tile) the boom ends in a dark point.
 */

// --- Shared ----------------------------------------------------------------------------------------------------------

type Pt = readonly [number, number];
type Curve = readonly [Pt, Pt, Pt];
const bezier = ([a, b, c]: Curve, t: number): [number, number] => {
  const s = 1 - t;
  return [s * s * a[0] + 2 * s * t * b[0] + t * t * c[0], s * s * a[1] + 2 * s * t * b[1] + t * t * c[1]];
};
/** How far (x, y) is from `curve` and where along it (0..1), sampled. */
function toCurve(curve: Curve, x: number, y: number): [number, number] {
  let [d, at] = [Infinity, 0];
  for (let t = 0; t <= 1; t += 0.01) {
    const [cx, cy] = bezier(curve, t);
    const e = Math.hypot(x - cx, y - cy);
    if (e < d) [d, at] = [e, t];
  }
  return [d, at];
}
/** Light on a surface whose normal is (u, v, z) in picture space (x right, y down, z toward us): from the top-left. */
const light = (u: number, v: number, z: number) => -0.62 * u - 0.55 * v + 0.55 * z;
const pick = <T>(ramp: readonly T[], l: number, cuts: readonly number[]): T =>
  ramp[cuts.filter((c) => l > c).length] as T;

/** The cap's satin, dark to lit: Creator violet (`P`, the gun's), shaded in navy (toward blue), a magenta sheen. */
const SATIN = ['1', 'P', 'p'] as const satisfies readonly PaletteKey[];
/** The headset's white plastic, dark to lit. */
const PLASTIC = ['3', '4', '5', 'w'] as const satisfies readonly PaletteKey[];
/** The mic's black foam, dark to lit. */
const FOAM_TONES = ['k', '1', '2'] as const satisfies readonly PaletteKey[];

/** Whether the face (looks.ts) has a pixel at frame (x, y). */
const onFace = (x: number, y: number) => FACE[0].get(x - FACE[1], y - FACE[2]) !== null;

// --- The cap and headset ---------------------------------------------------------------------------------------------

/** The cap's crown (frame px): a dome over the head down to its edge, lower at the back of the head. */
const CROWN = { cx: 29.5, cy: 27, rx: 20.5, ry: 15 } as const;
const capEdge = (x: number) => 27.6 + (50 - x) * 0.075;
function inCrown(x: number, y: number): boolean {
  if (y > capEdge(x)) return false;
  const u = (x - CROWN.cx) / CROWN.rx;
  return y >= CROWN.cy ? Math.abs(u) <= 1 : u * u + ((y - CROWN.cy) / CROWN.ry) ** 2 <= 1;
}
/** The bill, worn backwards: out over the back of the head, its top in the team's colour. */
const BILL = { cx: 10, cy: 27.2, rx: 8.6, ry: 3.6 } as const;
/** The snapback's opening over the brow, the fringe in it. */
const GAP = { cx: 41, r: 4.6 } as const;
/** Seams from the crown's top down to its edge. */
const SEAMS: readonly Curve[] = [
  [
    [29.5, 12.5],
    [39, 12.5],
    [41, 24.6],
  ],
  [
    [29.5, 12.5],
    [21, 14],
    [19.5, 29.6],
  ],
];
/** The headband over the crown, from the near ear cup to the far one. */
const BAND: Curve = [
  [16.5, 29],
  [27, -7.5],
  [49.5, 25.5],
];
/** The ear cups: the near one over the ear, the far one peeking past the face. */
const CUP = { cx: 17, cy: 35.5, rx: 5.6, ry: 7.2 } as const;
const FAR_CUP = { cx: 50.6, cy: 30.5, rx: 2.8, ry: 5.4 } as const;
/** The mic's boom from the near cup along the jaw, and its foam at the corner of the mouth. */
const BOOM: Curve = [
  [21.5, 40.5],
  [26, 44.6],
  [31, 42.4],
];
const FOAM = { cx: 32.6, cy: 41.8, rx: 2.3, ry: 1.9 } as const;
/** ▶ on the near cup. */
const PLAY = PixelCanvas.fromGrid(`
  w...
  ww..
  www.
  ww..
  w...
`);

const norm = (e: { cx: number; cy: number; rx: number; ry: number }, x: number, y: number) =>
  ((x - e.cx) / e.rx) ** 2 + ((y - e.cy) / e.ry) ** 2;

/**
 * The cap and headset standing (64 × 52 from the frame's top-left), over the face in the hair's place: the far cup
 * behind the head, the bill out over the back of it, the crown (seams, the team's trim, the snapback's opening over the
 * brow), the headband over it, the near cup over the ear with its ▶, the mic.
 */
function capAndHeadset(): Part {
  const c = new PixelCanvas(64, 52);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      // The far cup, behind the head: its shell in shadow, the team's plate a sliver on its outer edge.
      if (norm(FAR_CUP, px, py) <= 1 && !onFace(x, y)) {
        const u = (px - FAR_CUP.cx) / FAR_CUP.rx;
        c.set(
          x,
          y,
          u > 0.45 ? 'B' : pick(PLASTIC, -u * 0.6 - ((py - FAR_CUP.cy) / FAR_CUP.ry) * 0.5, [-0.3, 0.1, 0.5]),
        );
      }
      // The bill: the team's top, its lit edge, its underside in shadow.
      const inBill = (yy: number) =>
        px < BILL.cx + 1 && Math.abs((px - BILL.cx) / BILL.rx) ** 4 + ((yy - BILL.cy) / BILL.ry) ** 2 <= 1;
      if (inBill(py)) c.set(x, y, !inBill(py - 1) ? 'c' : !inBill(py + 1.6) ? 'B' : 'b');
      if (inCrown(px, py)) {
        const u = (px - CROWN.cx) / CROWN.rx;
        const v = py < CROWN.cy ? (py - CROWN.cy) / CROWN.ry : 0;
        let i = SATIN.indexOf(pick(SATIN, light(u, v, Math.sqrt(Math.max(0, 1 - u * u - v * v))), [-0.42, 0.95]));
        // Seams, a step darker.
        if (SEAMS.some((s) => toCurve(s, px, py)[0] < 0.55)) i = Math.max(0, i - 1);
        c.set(x, y, SATIN[i]!);
        // The trim round its edge in the team's colour, lit toward the back; over it the satin turns a step darker.
        if (py > capEdge(px) - 2.2) c.set(x, y, py < capEdge(px) - 1.2 ? (px < 26 ? 'c' : 'b') : px < 38 ? 'b' : 'B');
        else if (py > capEdge(px) - 1) c.set(x, y, SATIN[Math.max(0, i - 1)]!);
        // The snapback's opening over the brow: the fringe (black hair) in an arch on the trim, a white snap on it.
        const g = Math.hypot(px - GAP.cx, py - (capEdge(GAP.cx) - 2));
        if (g <= GAP.r && py < capEdge(px) - 2.2) c.set(x, y, g > GAP.r - 1.1 ? '1' : px < GAP.cx - 1.5 ? '3' : '2');
        if (Math.abs(px - GAP.cx) < 1 && py > capEdge(px) - 2.2) c.set(x, y, py < capEdge(px) - 1.2 ? 'w' : '5');
      }
      // The headband: white over its dark cushion.
      const [d, t] = toCurve(BAND, px, py);
      if (d < 1.6) {
        // Its inner edge (nearer the crown's middle than the band's spine) is the cushion, seen only over the cap.
        const [qx, qy] = bezier(BAND, t);
        const inner = Math.hypot(px - CROWN.cx, py - CROWN.cy) < Math.hypot(qx - CROWN.cx, qy - CROWN.cy) - 0.4;
        const l = light((px - CROWN.cx) / CROWN.rx, (py - CROWN.cy) / CROWN.ry, 0.4) + (d < 0.6 ? 0 : 0.35);
        if (!inner || inCrown(px, py)) c.set(x, y, inner ? '1' : pick(PLASTIC, l, [-0.4, 0, 0.45]));
      }
      // The near cup: a white shell round the team's plate.
      const n = norm(CUP, px, py);
      if (n <= 1) {
        const [u, v] = [(px - CUP.cx) / CUP.rx, (py - CUP.cy) / CUP.ry];
        const l = light(u, v, Math.sqrt(Math.max(0, 1 - n)));
        if (n > 0.5) c.set(x, y, pick(PLASTIC, -u * 0.7 - v * 0.7, [-0.75, -0.2, 0.35]));
        else c.set(x, y, pick(['B', 'b', 'c'] as const, l, [0.2, 0.75]));
      }
      // The mic: a white boom along the jaw, lit along its top, and black foam.
      const [b, at] = toCurve(BOOM, px, py);
      if (b < 0.95) c.set(x, y, py < bezier(BOOM, at)[1] ? '5' : '4');
      if (norm(FOAM, px, py) <= 1) {
        const [u, v] = [(px - FOAM.cx) / FOAM.rx, (py - FOAM.cy) / FOAM.ry];
        c.set(x, y, pick(FOAM_TONES, light(u, v, 0.5), [-0.3, 0.45]));
      }
    }
  }
  c.blit(PLAY, Math.round(CUP.cx - 2), Math.round(CUP.cy - 2.5));
  return [c, 0, 0];
}

const NONE: Part = [new PixelCanvas(1, 1), 0, 0];
const NO_TAILS: Record<RibbonKey, Part> = { hang: NONE, hang2: NONE, flowA: NONE, flowB: NONE, flowC: NONE };

/** Lying (seen from above, the chin left, the crown right): the cap over the crown, its edge across the brow. */
const LYING = { cx: 51, cy: 56.5, rx: 9.5, ry: 12.5 } as const;
const lyingEdge = (y: number) => 42.5 + ((y - LYING.cy) / 12) ** 2 * 4;
const LYING_SEAMS: readonly Curve[] = [
  [
    [58.5, 56.5],
    [56, 47],
    [44, 47.5],
  ],
  [
    [58.5, 56.5],
    [56, 66],
    [44, 65.5],
  ],
];
/** The headband round the crown, the cups at either side of the head, the boom to the mouth. */
const LYING_BAND: Curve = [
  [46, 44.5],
  [72.5, 56.5],
  [46, 68.5],
];
const TOP_CUP = { cx: 46, cy: 43.6, rx: 4.6, ry: 2.8 } as const;
const LOW_CUP = { cx: 46, cy: 67.4, rx: 4.6, ry: 2.6 } as const;
const LYING_BOOM: Curve = [
  [42.5, 66.5],
  [37, 67],
  [33.5, 62.5],
];
const LYING_FOAM = { cx: 32.5, cy: 61, rx: 1.9, ry: 2.2 } as const;

/**
 * The cap and headset on the dead pose's head: the cap's dome over the crown (its edge across the brow; the bill is under
 * the head), the band round the crown, a cup either side of the head (their team plates outward), the mic's boom along
 * the jaw.
 */
function deadCap(): Part {
  const c = new PixelCanvas(64, 72);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      if (norm(LYING, px, py) <= 1 && px >= lyingEdge(py)) {
        const [u, v] = [(px - LYING.cx) / LYING.rx, (py - LYING.cy) / LYING.ry];
        let i = SATIN.indexOf(pick(SATIN, light(u, v, Math.sqrt(Math.max(0, 1 - u * u - v * v))) + 0.15, [-0.42, 0.9]));
        if (LYING_SEAMS.some((s) => toCurve(s, px, py)[0] < 0.55)) i = Math.max(0, i - 1);
        if (px < lyingEdge(py) + 1) i = Math.max(0, i - 1);
        c.set(x, y, SATIN[i]!);
      }
      const [d, t] = toCurve(LYING_BAND, px, py);
      if (d < 1.6) {
        // Its outer edge (farther from the head than the band's spine) is the white shell, the inner the cushion.
        const [qx, qy] = bezier(LYING_BAND, t);
        const outer = Math.hypot(px - 46, py - LYING.cy) > Math.hypot(qx - 46, qy - LYING.cy);
        c.set(x, y, outer ? (py < LYING.cy ? 'w' : '5') : d > 0.6 ? '1' : py < LYING.cy + 3 ? '5' : '4');
      }
      for (const [cup, out] of [
        [TOP_CUP, -1],
        [LOW_CUP, 1],
      ] as const) {
        if (norm(cup, px, py) > 1) continue;
        const v = (py - cup.cy) / cup.ry;
        c.set(x, y, v * out > 0.25 ? (out < 0 ? 'c' : 'B') : v * out > -0.2 ? 'b' : px < cup.cx ? 'w' : '5');
      }
      const [b, at] = toCurve(LYING_BOOM, px, py);
      if (b < 0.95) c.set(x, y, px < bezier(LYING_BOOM, at)[0] ? '5' : '4');
      if (norm(LYING_FOAM, px, py) <= 1) {
        const [u, v] = [(px - LYING_FOAM.cx) / LYING_FOAM.rx, (py - LYING_FOAM.cy) / LYING_FOAM.ry];
        c.set(x, y, pick(FOAM_TONES, light(u, v, 0.5), [-0.3, 0.45]));
      }
    }
  }
  return [c, 0, 0];
}

const CAP = (): Worn => ({ worn: true, front: capAndHeadset(), ribbon: NO_TAILS, dead: deadCap() });

// --- The jacket ------------------------------------------------------------------------------------------------------

const JACKET_TOP = PixelCanvas.fromGrid(`
      ....wwppppPPPPPwcSSSSSB5PPPP1155....
      ..ww55pPPPbbbPPwccsssBB5PPPP115555..
      .www555pPbwbbBPwwcbsbB55PPPP1444444.
      wwww5555PbwwbBPPPPbbbPPPPPPP44444444
      ccbbbbbbPbwbbBPPPPPwPPPPPP1bbbbbBBBB
      .BBBBBBBBPBBBPPPPPP1PPPPPPBBBBBBBBB.
      ....pPPPPPPPPPPPPPPwPPPPPP111111....
      ....pPPPPPPPPPPPPPP1PPPPPP111111....
      ....wwwwwwwwwwwwwwww555555555555....
      ....1111111111111111111111111111....
`);

const JACKET: Outfit = {
  torso: [JACKET_TOP, 12, 44],
  dead: [
    PixelCanvas.fromGrid(`
      ....bwww
      ...cw555
      1wpPPPP5
      1wpPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPPP
      1wPPPPP1
      15PPPPP1
      15PPPP11
      15PPP111
      ...B4444
      ....B444
    `),
    24,
    51,
  ],
};

// --- The gun ---------------------------------------------------------------------------------------------------------

/**
 * The Creator finish (ADR-0128, moved here with its set): Twitch's blue-violet (#9146FF) in the palette's terms. Every
 * body ramp turns one flat violet (`P`), shaded in deep blue (`B`) and lit in white glints (Twitch's own pair, purple
 * and white): the blue shade pulls `P`'s red-violet toward Twitch's hue, where Royal (plum shade, pink light, gold brass)
 * and Bubblegum (pink) lean red. The outline steps stay as drawn; `B` is as dark as the `2` and `n` it paints. Rubric
 * (every gun at 8× and 1:1 beside Royal and Bubblegum, the wardrobe, a co-op round): R5 P4 L4 F4. Still weak: up close a
 * big steel shade is a blue block (a shotgun's receiver), and on blue its shade sits by the team's cuffs.
 */
const CREATOR_FINISH = {
  '2': 'B',
  '3': 'P',
  '4': 'P',
  '5': 'w',
  N: 'k',
  n: 'B',
  m: 'P',
  t: 'P',
  e: 'w',
  U: 'B',
  Y: 'P',
  y: 'w',
} as const satisfies Partial<Record<PaletteKey, PaletteKey>>;

export const CREATOR = drawnLater({ head: CAP, outfit: () => JACKET, gun: CREATOR_FINISH });
